import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {userIdKey} from '@/constants';
import {CookieSerializeOptions} from '@/services/CookieStorage/types';
import {GlobalWindow} from '@/types';

enum CookieKeys {
  UserId = userIdKey,
  UseSSOMocks = 'use-sso-mocks',
}

export class CookieStorage {
  // 2 месяца в секундах для использования
  // в качестве дефолтного значения max-age
  private readonly defaultMaxAge = 60 * (60 * (24 * 60));

  constructor(private readonly globalWindow: GlobalWindow) {}

  public getUserId = (): string | undefined => {
    return this.getCookie(CookieKeys.UserId);
  };

  public setUserId = (userId: string): void => {
    this.setCookie(CookieKeys.UserId, userId);
  };

  public deleteUserId = (): void => {
    this.deleteCookie(CookieKeys.UserId);
  };

  public getUseSSOMocks(): string | undefined {
    return this.getCookie(CookieKeys.UseSSOMocks);
  }

  public setUseSSOMocks(value: string): void {
    this.setCookie(CookieKeys.UseSSOMocks, value);
  }

  public getAllCookies = (): string => {
    return this.globalWindow.document.cookie;
  };

  private getCookie(name: CookieKeys): string | undefined {
    const parsedCookie = this.globalWindow.document.cookie
      .split(';')
      .map((v) => v.split('='))
      .reduce<Record<string, string>>((acc, v) => {
        // Если во время загрузки страницы в инкогнито дергать getCookie,
        // то можно начать парсить пустую строку и словить ошибку на .trim()
        if (v.length !== 2) {
          return acc;
        }

        acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim());
        return acc;
      }, {});

    return parsedCookie[name];
  }

  private setCookie(name: string, value: string, options: CookieSerializeOptions = {}): void {
    // По умолчанию задаем время жизни куки два месяца,
    // потому что проект идет один месяц и еще один месяц берем с запасом
    const maxAge = typeof options['max-age'] === 'number' ? options['max-age'] : this.defaultMaxAge;

    options = {
      path: '/',
      ['max-age']: maxAge,
      ...options,
    };

    let updatedCookie = encodeURIComponent(name) + '=' + encodeURIComponent(value);

    for (const optionKey in options) {
      updatedCookie += '; ' + optionKey;
      const optionValue = options[optionKey as keyof typeof options];
      if (optionValue !== true) {
        updatedCookie += '=' + optionValue;
      }
    }

    this.globalWindow.document.cookie = updatedCookie;
  }

  private deleteCookie(name: CookieKeys): void {
    this.setCookie(name, '', {
      'max-age': -1,
    });
  }
}

injected(CookieStorage, DI_TOKENS.globalWindow);
